import { useEffect, useMemo, useState } from 'react';
import {
  experiment,
  tracking,
  useMultipleExperiments,
} from '@podiumhq/tracking';

import useQueryParams from 'hooks/useQueryParams';
import {
  CHECKOUT_MULTI_STEP_FLAG,
  CHECKOUT_REARRANGE_STEPS_FLAG,
  NCCT_STEPS_FLAG,
  BUY_NOW_WITH_DISCOUNT,
} from 'lib/amplitude/flags.constants';
import { getCookies } from 'lib/getCookies';

export const useTrialTrackingData = () => {
  const [loadingFlags, flags] = useMultipleExperiments([
    CHECKOUT_MULTI_STEP_FLAG,
    CHECKOUT_REARRANGE_STEPS_FLAG,
    NCCT_STEPS_FLAG,
    BUY_NOW_WITH_DISCOUNT,
  ]);
  const { value: salesforceCampaignIdFromParams } = useQueryParams('scid');
  const { value: viewFlow } = useQueryParams('view');
  const { value: utmMediumFromParams } = useQueryParams('utm_medium');

  const [firstUtmMedium, setFirstUtmMedium] = useState(null);
  const [googleClientId, setGoogleClientId] = useState('');
  const [partnerId, setPartnerId] = useState(null);
  const [salesforceCampaignIdFromCookies, setSalesforceCampaignIdFromCookies] =
    useState(null);
  const [utmMediumFromCookies, setUtmMediumFromCookies] = useState(null);
  const [ssqt, setSsqt] = useState('');
  const [fbclid, setFbclid] = useState('');
  const [msclkid, setMsclkid] = useState('');
  const [googleClickId, setGoogleClickId] = useState('');

  const isMultiStepFlow =
    flags[CHECKOUT_MULTI_STEP_FLAG]?.value === 'on' || viewFlow === 'multi';
  const isRearrangeStepsFlow =
    flags[CHECKOUT_REARRANGE_STEPS_FLAG]?.value === 'on' ||
    viewFlow === 'rearrange';
  const isNCCTFlow =
    flags[NCCT_STEPS_FLAG]?.value === 'on' || viewFlow === 'ncct';
  const showDiscountPricing =
    flags[BUY_NOW_WITH_DISCOUNT]?.value === 'on' || viewFlow === 'buy-now';
  const isRoiFlow = viewFlow === 'roi2999';

  const getTrialVersion = () => {
    if (isMultiStepFlow) return 'v2';
    if (isRearrangeStepsFlow) return 'v2.2';
    // default is NCCT flow so updating to reflect that
    return 'v4';
  };

  const flows = useMemo(() => {
    return {
      isMultiStepFlow,
      isRearrangeStepsFlow,
      showMultiStep: isMultiStepFlow || isRearrangeStepsFlow,
      isNCCTFlow,
      viewFlow,
      isRoiFlow,
    };
  }, [viewFlow, isMultiStepFlow, isRearrangeStepsFlow, isNCCTFlow, isRoiFlow]);

  useEffect(() => {
    if (loadingFlags) return;
    const trialVersion = getTrialVersion();

    let userProperties = { 'Checkout Trial Version': trialVersion };

    const allCookies = getCookies();
    const clientId = allCookies['_ga']?.split('.')?.slice(-2)?.join('.');

    setFirstUtmMedium(allCookies['first-utm-medium']);
    setGoogleClientId(clientId);
    setPartnerId(allCookies['partner-id']);
    setSalesforceCampaignIdFromCookies(allCookies['scid']);
    setUtmMediumFromCookies(allCookies['utm-medium']);
    setSsqt(allCookies['ssqt']);
    setFbclid(allCookies['fbclid']);
    setMsclkid(allCookies['msclkid']);
    setGoogleClickId(allCookies['gclid']);

    if (clientId) {
      userProperties = { ...userProperties, 'Google Client Id': clientId };
    }

    tracking.setUserProperties(userProperties);

    // to track specific users in the future for NCCT flow only
    if (!isMultiStepFlow && !isRearrangeStepsFlow) {
      experiment.fetch({ free_trial_signup: 'true' });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadingFlags]);

  return useMemo(
    () => ({
      ...flows,
      loadingFlags,
      firstUtmMedium,
      googleClickId,
      googleClientId,
      partnerId,
      salesforceCampaignId:
        salesforceCampaignIdFromCookies || salesforceCampaignIdFromParams,
      utmMedium: utmMediumFromCookies || utmMediumFromParams,
      ssqt,
      showDiscountPricing,
      fbclid,
      msclkid,
    }),
    [
      loadingFlags,
      firstUtmMedium,
      flows,
      googleClickId,
      googleClientId,
      partnerId,
      salesforceCampaignIdFromCookies,
      salesforceCampaignIdFromParams,
      utmMediumFromCookies,
      utmMediumFromParams,
      ssqt,
      showDiscountPricing,
      fbclid,
      msclkid,
    ]
  );
};

export default useTrialTrackingData;
